import { DashboardInsights, PatientSummaryStatistics } from '@bioliberty/lifemind';
import { getQueryCacheKey } from '../util';
import { downloadExportResponse } from './export-download';
import { tms } from './tms';

const cacheTag = 'Insights';

interface KpiParams {
  tenantIds?: string[];
  patientIds?: number[];
  activityTypeIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

export type ActivityTimeSeriesGranularity = 'hour' | 'day' | 'week';

export type GetPatientActivitySummaryStatisticsResult = PatientSummaryStatistics & {
  [index: string]: number | undefined;
};

export type GetInsightsDashboardParams = KpiParams & {
  granularity?: ActivityTimeSeriesGranularity;
  activityTypeIds?: string[];
};

export type GetInsightsExportParams = KpiParams & { format?: string };

const insights = tms.injectEndpoints({
  endpoints: (builder) => ({
    getInsightsDashboard: builder.query<DashboardInsights, GetInsightsDashboardParams>({
      query: (params) => ({
        url: `insights/dashboard`,
        method: 'GET',
        params: {
          ...params,
          tenantIds: params.tenantIds?.join(','),
          patientIds: params.patientIds?.join(','),
          activityTypeIds: params.activityTypeIds?.join(',') || undefined,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      }),
      providesTags: (_result, _error, params) => [{ type: cacheTag, id: getQueryCacheKey(params, 'dashboard') }]
    }),
    getInsightsExport: builder.mutation<void, GetInsightsExportParams>({
      query: (params) => ({
        url: `insights/export`,
        method: 'GET',
        params: {
          ...params,
          tenantIds: params.tenantIds?.join(',')
        },
        responseHandler: downloadExportResponse,
        cache: 'no-store'
      })
    }),
    getPatientActivitySummaryStatistics: builder.query<GetPatientActivitySummaryStatisticsResult, KpiParams>({
      query: (params) => ({
        url: `patients/insights/summary-statistics`,
        method: 'GET',
        params: {
          ...params,
          tenantIds: params.tenantIds?.join(','),
          patientIds: params.patientIds?.join(','),
          activityTypeIds: params.activityTypeIds?.join(',') || undefined
        }
      }),
      providesTags: (_result, _error, params) => [
        { type: cacheTag, id: getQueryCacheKey(params, 'summary-statistics') }
      ]
    })
  }),
  overrideExisting: false
});

export const { useQuery: useGetPatientActivitySummaryStatisticsQuery } =
  insights.endpoints.getPatientActivitySummaryStatistics;

export const { useQuery: useGetInsightsDashboardQuery } = insights.endpoints.getInsightsDashboard;

export const { useMutation: useGetInsightsExport } = insights.endpoints.getInsightsExport;
